import { TextArea } from "@tql/ui-components-react/kendo/inputs";
import { Hint, Label } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { useContext, useEffect, useRef, useState } from "react";

import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { formatSpecialInstructions } from "~/features/quote/utils/format-quote-state.ts";

import { SpecialInstructionsModal } from "../special-instructions-modal/special-instructions-modal.tsx";

type SpecialInstructionsSectionProps = {
	hideSaveInstructions?: boolean;
};

export const SpecialInstructionsSection = ({
	hideSaveInstructions = false,
}: SpecialInstructionsSectionProps) => {
	const { quoteState, onChange } = useContext(QuoteDetailsContext);
	const { selectedAddressBookItems, addressChangeSourceRef } =
		useQuoteAddressContext();
	const canSaveShipper =
		!!selectedAddressBookItems.pickup?.addressBookItemId &&
		!selectedAddressBookItems.pickup?.isLegacy;
	const canSaveReceiver =
		!!selectedAddressBookItems.delivery?.addressBookItemId &&
		!selectedAddressBookItems.delivery?.isLegacy;
	const [showSpecialInstructionsModal, setShowSpecialInstructionsModal] =
		useState(false);
	const lastAppliedAddressInstructionsRef = useRef("");
	const specialInstructionsRef = useRef(quoteState.specialInstructions || "");
	specialInstructionsRef.current = quoteState.specialInstructions || "";

	const toggleSpecialInstructionsModal = () => {
		setShowSpecialInstructionsModal(!showSpecialInstructionsModal);
	};

	const max = 500;

	useEffect(() => {
		const newInstructions = formatSpecialInstructions(selectedAddressBookItems);
		const current = specialInstructionsRef.current;
		const lastApplied = lastAppliedAddressInstructionsRef.current;

		if (newInstructions && current.trim() === newInstructions.trim()) {
			lastAppliedAddressInstructionsRef.current = newInstructions;
			return;
		}

		const isAutoHydration = addressChangeSourceRef.current === "auto";
		const canApplyAutoHydration =
			!isAutoHydration ||
			current.trim() === "" ||
			(Boolean(lastApplied) && current.trim() === lastApplied.trim());

		if (newInstructions && canApplyAutoHydration) {
			let manualText = current;
			if (lastApplied) {
				manualText = manualText.replace(lastApplied, "").trim();
			}

			const combined = manualText
				? `${manualText}\n${newInstructions}`
				: newInstructions;

			if (combined === current) return;

			lastAppliedAddressInstructionsRef.current = newInstructions;
			onChange("specialInstructions", combined);
		}
	}, [onChange, selectedAddressBookItems, addressChangeSourceRef]);

	return (
		<>
			<div className={"special-instructions-wrapper additional-details-value"}>
				<div className={"special-instructions"}>
					<Label editorId={"specialInstructions"}>Special Instructions</Label>
					<TextArea
						id={"specialInstructions"}
						maxLength={max}
						name={`specialInstructions`}
						onChange={({ value }: { value: string }) => {
							onChange("specialInstructions", value);
						}}
						placeholder={`${max} Characters...`}
						rows={3}
						value={quoteState.specialInstructions || ""}
					/>
					<Hint direction={"end"}>
						{(quoteState.specialInstructions || "").length} / {max}
					</Hint>
				</div>
				<div className={"save-instructions-button"}>
					{hideSaveInstructions ? null : (
						<Button
							disabled={!canSaveShipper && !canSaveReceiver}
							onClick={toggleSpecialInstructionsModal}
							tabIndex={-1}
						>
							Save Instructions
						</Button>
					)}
				</div>
			</div>
			{showSpecialInstructionsModal ? (
				<SpecialInstructionsModal
					toggleModal={toggleSpecialInstructionsModal}
				/>
			) : null}
		</>
	);
};
